<template>
  <div>
    <div v-if="config && !config.active" role="alert" aria-live="assertive">
      <CenteredContainer>
        <SvgIcon name="no" width="35rem" style="opacity: 0.2;" aria-hidden="true" />
        <h2>{{ $t('deactivatedMessage') }}</h2>
      </CenteredContainer>
    </div>
    <div v-else>
      <BarcodeScanner v-if="storeOpen" @scan="handleScan" aria-label="Barcode Scanner" role="application" />
      <IdleScreen v-if="storeOpen && config.screensaverEnabled" @is-visible="handleIdleScreenVisible" :start-order-text="$t('startOrder')"
        :showText="config.screensaverShowText" :start-order-subtext="$t('startOrderSubtext')"
        :enabled="config.screensaverEnabled" :orderTypes="orderTypes" :backgroundType="config.screensaverBackgroundType"
        :videoSource="config.screensaverVideoSource" :idle-time-seconds="4" @selectSingleOrderType="orderTypeSelected"
        @showOrderTypes="showOrderTypes" :order-started="started" />
      <div v-if="!showScreensaverContent">
        <question-asker v-if="showQuestions && started" :questions="questionsToAsk" @cancel="handleQuestionCancel"
          @complete="handleQuestionComplete" @signin="handleSignIn" />
        <CenteredContainer v-if="details" :class="fadeInClass">
          <div class="top-right">
            <LanguageSelector v-if="config" :class="languageSelectorClass"
              :default-language="config.languageSupport.defaultLanguage" :enabled="config.languageSupport.enabled"
              :supported-languages="config.languageSupport.supportedLanguages" />
          </div><br />
          <LocationLogo :name="details.name" style="width: 8.25rem; height: 8.25rem" v-if="config.logo?.url" :logo="config.logo?.url" :shape="config.logo?.shape || 'circle'" />
          <p class="welcome-message">
            <ConfigText textKey="welcomeMessage" :initialValue="$t('welcomeMessage')" />
          </p>
          <p v-if="!storeOpen" class="opening-time">{{ storeOpeningTimeMessage }}</p>
          <div v-else>
            <p class="order-type-question" @click="launchRockets">
              <ConfigText textKey="selectOrderTypeQuestion" :initialValue="$t('selectOrderTypeQuestion')" />
            </p>
            <div class="d-flex justify-content-center animated-button">
              <ButtonLarge v-for="(orderType, index) in orderTypes" :key="orderType.type" :name="orderType.name"
                :icon="orderType.icon" :use-icon="config.icons" :style="{ animationDelay: `${index * 0.2}s` }"
                class="col-6 align-items-center animated-button" @buttonClicked="orderTypeSelected(orderType)"
                @touchstart="handleTouchStart(orderType)" @touchend="handleTouchEnd(orderType)" :class="{
                  'not-selected': selectedOrderType && (orderType !== selectedOrderType),
                  'active': selectedOrderType && (orderType === selectedOrderType) || orderType._touchActive
                }" />
            </div>
          </div>
        </CenteredContainer>
      </div>
    </div>
    <div class="version-number">{{ feVersion }}</div>
  </div>
</template>

<script>
import LanguageSelector from "@/components/features/LanguageSelector.vue";
import { mapGetters } from "vuex";
import { openingSoonMessage, isOpen } from "@/utils/time";
import CenteredContainer from "@/components/global/CenteredContainer.vue";
import LocationLogo from "@/components/global/LocationLogo.vue";
import ButtonLarge from "@/components/global/ButtonLarge.vue";
import IdleScreen from "@/components/features/IdleScreen.vue";
import QuestionAsker from "@/components/features/QuestionAsker.vue";
import BarcodeScanner from "@/components/features/BarcodeScanner.vue";
import logger from "@/utils/logger";

export default {
  inject: ['translator'],
  name: "StartView",
  components: {
    CenteredContainer,
    LocationLogo,
    ButtonLarge,
    IdleScreen,
    QuestionAsker,
    BarcodeScanner,
    LanguageSelector,
  },
  data() {
    return {
      showQuestions: false,
      questionsToAsk: [],
      storeOpen: false,
      selectedOrderType: null,
      showScreensaverContent: false,
      fadeInClass: "fade-in",
      languageSelectorClass: "animate__animated animate__slideInDown",
    };
  },
  computed: {
    ...mapGetters(["feVersion"]),
    ...mapGetters("location", ["details", "config", "orderTypes"]),
    ...mapGetters("order", ["type", "started"]),
    ...mapGetters("menu", ["schedules"]),
    storeOpeningTimeMessage() {
      if (!this.storeOpen) {
        return openingSoonMessage(this.schedules);
      }
      return "";
    },
  },
  watch: {
    orderTypeRef: {
      immediate: true,
      handler(orderTypeRef) {
        if (orderTypeRef) {
          console.log("Order type ref found in URL, selecting order type");
          const orderType = this.orderTypes.find((ot) => ot.id === orderTypeRef);
          this.orderTypeSelected(orderType);
          // remove orderTypeRef from url
          this.$router.replace({ query: {} });

        }
      },
    },
  },
  props: {
    orderTypeRef: {
      type: String,
      default: null
    }
  },
  methods: {
    handleIdleScreenVisible(val) {
      if(!val) {
          this.fadeInClass = "fade-in";
          this.languageSelectorClass = "animate__animated animate__slideInDown";
        } else {
          this.fadeInClass = "";
          this.languageSelectorClass = "";
        }
    },
    handleTouchStart(orderType) {
      if (orderType === this.selectedOrderType) return;
      orderType._touchActive = true;
    },
    handleTouchEnd(orderType) {
      if (orderType === this.selectedOrderType) return;
      orderType._touchActive = false;
      this.orderTypeSelected(orderType);
    },
    prepareQuestionsToAsk(questions) {
      const keysToTranslate = ['email', 'phone', 'badge', 'name'];
      return questions.map(question => {

        if (this.translator.currentLanguage === this.config.languageSupport.defaultLanguage) {
          return question;
        }

        const key = keysToTranslate.find(k => question.title.toLowerCase().includes(k));
        console.log('Key to translate', key);
        if (key) {
          console.log('Translating question', key);
          question.title = this.$t(`${key}Question`);
          question.text = '';
        }
        return question;
      });
    },
    checkStoreStatus() {
      this.storeOpen = isOpen(this.schedules);
    },
    async orderTypeSelected(orderType, barcode = null) {
      this.checkStoreStatus();
      if (!this.type) {
        logger.info(`Order type selected: ${orderType.name}`);
      } else {
        logger.info(`Order type changed from ${this.type} to ${orderType.name}`);
      }
      this.$store.dispatch("order/start");
      this.$store.dispatch("order/setTimeoutActive", true);
      this.selectedOrderType = orderType;

      this.questionsToAsk = [];

      if (this.config.phoneLogin) {
        this.questionsToAsk = [{
          title: "Sign In",
          text: "Please enter your phone number",
          inputType: "tel",
          optIn: true,
          orderProperty: "phone",
          optInText: "Remember my personal details by phone",
          validationRegex: "\\d{10}",
          placeholder: "(555) 555-5555",
          required: false
        }];
      }

      const orderTypeQuestions = orderType.orderDetails?.filter(d => d.timing == 'startOrder') || [];
      const theQuestions = [...this.questionsToAsk, ...orderTypeQuestions];



      this.questionsToAsk = this.prepareQuestionsToAsk(theQuestions);

      logger.info(`Questions prepared for display: ${this.questionsToAsk.length} questions`);

      if (!this.questionsToAsk.length) {
        await this.$store.dispatch("order/setOrderType", orderType);
        this.$store.dispatch("order/setBarcodeLookup", barcode);
        if (this.type) {
          setTimeout(() => {
            this.$router.push({ name: "menu" });
          }, 100);

        }
      } else {
        setTimeout(() => {
          this.showQuestions = true;
        }, 100);
      }
    },
    selectSingleOrderType() {
      const singleOrderType = this.orderTypes[0];
      this.orderTypeSelected(singleOrderType);
      logger.info(`Single order type selected: ${singleOrderType.name}`);
    },
    showOrderTypes() {
      this.showScreensaverContent = false;
    },
    handleQuestionCancel() {
      this.showQuestions = false;
      this.questionsToAsk = [];
      logger.info('Question answering process canceled by the user');
      if (this.orderTypeRef) {
        this.$router.push({ name: "menu" });
      } else {
        this.$store.dispatch("order/reset");
      }
    },
    async handleSignIn(phone) {
      if (!phone) {
        return Promise.reject('Phone number is required');
      }
      try {
        this.$store.commit("order/SET_OPT_IN", true);
        await this.$store.dispatch("order/signIn", { phone: phone });
        return Promise.resolve();  // Ensure the promise resolves after sign-in
      } catch (error) {
        console.error(`Error signing in with phone number: ${phone}`, error);
        return Promise.reject(error);
      }
    },
    async handleQuestionComplete(answers) {
      console.log('Questions completed, processing answers');
      this.$store.dispatch("order/fillOrderDetails", answers);
      this.showQuestions = false;
      await this.$store.dispatch("order/setOrderType", this.selectedOrderType);
      this.$router.push({ name: "menu" });
    },
    handleScan(scannedCode) {
      logger.info(`Barcode scanned: ${scannedCode}`);
      this.proceedWithFirstRetailOrderType(scannedCode);
    },
    proceedWithFirstRetailOrderType(barcode) {
      const firstRetailOrderType = this.orderTypes.find(ot => ot.view === 'retail');
      if (firstRetailOrderType) {
        this.orderTypeSelected(firstRetailOrderType, barcode);
      } else {
        logger.error('No retail order type found');
      }
    },
    launchRockets() {
      logger.info('Launching rockets - special feature activated');
      for (let i = 0; i < 10; i++) {
        let rocket = document.createElement('div');
        rocket.innerHTML = `<svg width="132" height="182" viewbox="0 0 132 182" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.9597 134.01C2.2897 135.82 0.679688 138.84 0.679688 142.07V181.84L43.6897 153.57L36.2797 109.86L4.9597 134V134.01Z" fill="black"/>
      <path d="M126.951 134.01C129.621 135.82 131.231 138.84 131.231 142.07V181.84L88.2207 153.57L95.6307 109.86L126.951 134V134.01Z" fill="black"/>
      <path d="M65.9597 0.469971C-0.0903244 52.55 36.0897 158.56 36.0897 158.56H95.8297C95.8297 158.56 132.01 52.55 65.9597 0.469971Z" fill="#CED0D1"/>
      <path d="M36.0799 158.56H65.9499V0.469971C-0.100075 52.55 36.0799 158.56 36.0799 158.56Z" fill="white"/>
      <path d="M35.5098 41.54H96.3898C90.2898 26.35 80.6598 12.07 65.9498 0.469971C51.2398 12.07 41.6098 26.35 35.5098 41.54Z" fill="#136EF6"/>
      <path d="M88.0903 158.56H43.8203L37.8203 175.8H94.0903L88.0903 158.56Z" fill="black"/>
    </svg>`;
        rocket.className = 'rocket';
        document.body.appendChild(rocket);

        let sizeFactor = Math.random() * (1 - 0.1) + 0.1;
        let animationDuration = Math.random() * (5 - 2) + 2;

        rocket.style.width = `132px`;
        rocket.style.height = `182px`;
        rocket.style.position = 'fixed';
        rocket.style.transform = `scale(${sizeFactor}) rotate(45deg)`;
        rocket.style.transformOrigin = 'bottom left';

        let verticalSpread = (window.innerHeight / 2) * Math.random();
        let startX = -Math.random() * 200 - 200;
        let startY = window.innerHeight + (sizeFactor * 182) - verticalSpread;

        rocket.style.left = `${startX}px`;
        rocket.style.top = `${startY}px`;

        setTimeout(() => {
          rocket.style.transition = `transform ${animationDuration}s linear`;
          rocket.style.transform = `translate(${window.innerWidth + 1000}px, -${window.innerHeight + verticalSpread}px) scale(${sizeFactor}) rotate(45deg)`;
        }, 100);

        setTimeout(() => {
          document.body.removeChild(rocket);
        }, animationDuration * 1000 + 100);
      }
    },
  },
  mounted() {
    this.showQuestions = false;
    if (this.orderTypeRef) {
      const ot = this.orderTypes?.find(ot => ot.id === this.orderTypeRef);
      if (!ot) {
        logger.error(`Order type with id ${this.orderTypeRef} not found`);
        return;
      }
      this.orderTypeSelected(ot);
      return;
    }

    if (this.config?.restartEveryOrder && this.type) {
      logger.info('Restarting the order process due to configuration');
      window.location.reload();
    } else {
      this.$store.dispatch("order/reset");
      this.storeOpen = isOpen(this.schedules);
      this.intervalId = setInterval(this.checkStoreStatus, 10000);
    }
    this.$store.dispatch("order/setTimeoutActive", false);

    setTimeout(() => {
      this.fadeInClass = "fade-in";
    }, 1000);
  },
  unmounted() {
    clearInterval(this.intervalId);
  },
};
</script>

<style scoped>
.logo-hold {
  width: 8.25rem;
  height: 8.25rem;
}

.welcome-message {
  font-size: 2.125rem;
  margin-top: 1.5rem;
  margin-bottom: 5.75rem;
  color: var(--grey-medium);
}

.opening-time {
  font-size: 2.5rem;
  font-weight: 700;
}

.order-type-question {
  font-size: 2.75rem;
  font-weight: 700;
  margin-bottom: 5rem;
  text-align: center;
}

.btn-container {
  margin-right: 7.75rem;
}

.btn-container:last-child {
  margin-right: 0;
}

.vertical .d-flex {
  flex-direction: column;
  align-items: center;
}

.vertical .btn-container {
  margin-right: 0;
  margin-bottom: 7.75rem !important;
}

/* Adjust ButtonLarge component's width and margin for vertical layout */
.vertical .d-flex .col-6 {
  width: 100%;
  margin-bottom: 1rem;
}

/* Remove the margin from the last button to clean up spacing */
.vertical .d-flex .col-6:last-child {
  margin-bottom: 0;
}

.single-order-type {
  background-color: var(--brand-color);
  color: #fff;
  font-size: 8rem !important;
}

.single-order-message {
  font-size: 6rem;
  line-height: 9rem;
  font-weight: 100;
  text-align: center;
}

.top-right {
  position: absolute;
  top: 2.5rem;
  right: 2.5rem;
}

.animated-button {
  visibility: hidden;
  animation: fade-in-up 0.5s ease-in-out forwards;
}

@keyframes fade-in-up {
  0% {
    opacity: 0;
    transform: translateY(30px);
    visibility: visible;
  }

  100% {
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
  }
}

.animate__slideInDown {
  visibility: hidden;
  animation-duration: 0.8s !important;
  animation-fill-mode: forwards;
  visibility: visible;
}

.not-selected {
  transform: scale(0.95) !important;
  opacity: 0.5 !important;
}

.active {
  transform: scale(1.05) !important;
  opacity: 1 !important;
}

.version-number {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  font-size: 1.75rem;
  color: grey;
}
</style>
